import { CircularProgress } from '@mui/material';
import {
  confirmSignUp,
  getCurrentUser,
  resendSignUpCode,
  signUp,
} from 'aws-amplify/auth';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import { FC, FormEvent, useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { contentLanguage } from 'src/i18n/contentLanguage';
import { useNavigate, useParams } from 'react-router-dom';
import {
  OthRegistrationSchema,
  getOthRegistrationSchema,
  submitOthRegistration,
} from 'src/api/othRegistration';
import { usersApi } from 'src/api/user';
import { DunbarPhoneInput } from 'src/components/common/PhoneInput/PhoneInput';
import { MuiModal } from 'src/components/material/MuiModal';
import { useAppDispatch } from 'src/hooks/Redux/useAppDispatch';
import { useUserLoginLogout } from 'src/hooks/UserLoginLogout/UserLoginLogout';
import { fetchLoggedUser } from 'src/redux/slices/loggedUserSlice';
import { fetchUser } from 'src/redux/slices/userSlice';
import { Utils } from 'src/utils/utils';

import styles from './CircleRegistration.module.scss';
import {
  BACK_LABEL,
  CLOSE_LABEL,
  CODE_LABEL,
  CODE_PLACEHOLDER,
  CONFIRM_LABEL,
  CONFIRM_TEXT,
  CONFIRM_TITLE,
  CONSENT_LABEL,
  CREATE_ACCOUNT_LABEL,
  EMAIL_LABEL,
  ERROR_ACCOUNT_EXISTS,
  ERROR_CODE,
  ERROR_CODE_WRONG,
  ERROR_CONFIRM,
  ERROR_CONSENT,
  ERROR_EMAIL,
  ERROR_EMAIL_INVALID,
  ERROR_FIRST_NAME,
  ERROR_PASSWORD,
  ERROR_PASSWORD_WEAK,
  ERROR_PHONE,
  ERROR_PHONE_INVALID,
  ERROR_RESEND,
  ERROR_SIGNUP,
  ERROR_SUBMIT,
  ERROR_SURNAME,
  FIRST_NAME_LABEL,
  JOIN_NOW_LABEL,
  PASSWORD_HINT,
  PASSWORD_LABEL,
  PHONE_LABEL,
  PRIVACY_LINK_LABEL,
  PRIVACY_PARAGRAPHS,
  PRIVACY_TITLE,
  REGISTRATION_INTRO,
  REGISTRATION_LOADING_TEXT,
  REGISTRATION_UNAVAILABLE_TEXT,
  REGISTRATION_WRONG_KIND_TEXT,
  RESEND_LABEL,
  RESENT_TEXT,
  SIGNED_IN_INTRO,
  SUCCESS_TEXT,
  SURNAME_LABEL,
  WORKING_LABEL,
  circleTitle,
  isEmailLike,
  isPasswordStrong,
  splitFullName,
} from './registrationText';

type Step = 'checking' | 'account' | 'confirm' | 'signedIn';

interface Draft {
  firstName: string;
  surname: string;
  phone: string;
  email: string;
  password: string;
  code: string;
  consent: boolean;
}

const EMPTY_DRAFT: Draft = {
  firstName: '',
  surname: '',
  phone: '',
  email: '',
  password: '',
  code: '',
  consent: false,
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const errorName = (error: unknown): string =>
  typeof error === 'object' && error !== null && 'name' in error
    ? String((error as { name: unknown }).name)
    : '';

const errorMessage = (error: unknown): string =>
  typeof error === 'object' && error !== null && 'message' in error
    ? String((error as { message: unknown }).message)
    : '';

/**
 * `/registrering/:tribeId`: how a student joins a LONELINESS Circle
 * (docs/briefs/dunbar148-loneliness-plan.md section 5.3). One card: name,
 * phone, email, password and the privacy consent; an SMS code confirms the
 * phone; the registration is sent and the student lands in the Circle. A
 * signed-in user only confirms. OTH's own registration page is untouched.
 */
export const CircleRegistrationPage: FC = () => {
  const { tribeId = '' } = useParams<{ tribeId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { login } = useUserLoginLogout();

  const [schema, setSchema] = useState<OthRegistrationSchema | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>('checking');
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [signedInName, setSignedInName] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const update = (patch: Partial<Draft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  useEffect(() => {
    let cancelled = false;
    getOthRegistrationSchema(tribeId)
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.special_code !== 'LONELINESS') {
          setLoadError(REGISTRATION_WRONG_KIND_TEXT);
          return;
        }
        setSchema(result);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(REGISTRATION_UNAVAILABLE_TEXT);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [tribeId]);

  const waitForBackendUser = useCallback(async () => {
    let lastError: unknown = null;
    for (let attempt = 0; attempt < 20; attempt += 1) {
      try {
        return await usersApi.userMe();
      } catch (userError) {
        lastError = userError;
        await sleep(500);
      }
    }
    throw lastError ?? new Error('No user record');
  }, []);

  // A signed-in user (a leader testing, or a student who registered once)
  // only confirms; everybody else makes an account first.
  useEffect(() => {
    let cancelled = false;
    getCurrentUser()
      .then(async () => {
        const me = await usersApi.userMe();
        if (cancelled) {
          return;
        }
        const profile = me.result;
        const { firstName, surname } = splitFullName(profile.full_name || '');
        setSignedInName(profile.full_name || profile.phone_number || '');
        update({
          firstName,
          surname,
          phone: profile.phone_number || '',
          email: profile.email || '',
        });
        setStep('signedIn');
      })
      .catch(() => {
        if (!cancelled) {
          setStep('account');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const accountError = (): string | null => {
    if (!draft.firstName.trim()) {
      return ERROR_FIRST_NAME;
    }
    if (!draft.surname.trim()) {
      return ERROR_SURNAME;
    }
    const phone = Utils.formatPhone(draft.phone);
    if (!phone) {
      return ERROR_PHONE;
    }
    if (!isPossiblePhoneNumber(phone)) {
      return ERROR_PHONE_INVALID;
    }
    if (!draft.email.trim()) {
      return ERROR_EMAIL;
    }
    if (!isEmailLike(draft.email)) {
      return ERROR_EMAIL_INVALID;
    }
    if (step === 'account') {
      if (!draft.password) {
        return ERROR_PASSWORD;
      }
      if (!isPasswordStrong(draft.password)) {
        return ERROR_PASSWORD_WEAK;
      }
    }
    if (!draft.consent) {
      return ERROR_CONSENT;
    }
    return null;
  };

  const submitRegistration = async () => {
    const email = draft.email.trim().toLowerCase();
    const result = await submitOthRegistration(tribeId, {
      language: contentLanguage(),
      first_name: draft.firstName.trim(),
      surname: draft.surname.trim(),
      email,
      answers: {
        first_name: draft.firstName.trim(),
        surname: draft.surname.trim(),
        email,
        phone_number: Utils.formatPhone(draft.phone) || draft.phone,
        consent_privacy: true,
      },
    });
    await dispatch(fetchLoggedUser());
    toast.success(SUCCESS_TEXT);
    navigate(`/circle/${result.tribeId}`, { replace: true });
  };

  const signInAndSubmit = async () => {
    const phone = Utils.formatPhone(draft.phone);
    const signedIn = await login({ username: phone, password: draft.password });
    if (!signedIn?.isSignedIn) {
      throw new Error('login failed');
    }
    await dispatch(fetchUser());
    await waitForBackendUser();
    await submitRegistration();
  };

  const handleCreateAccount = async (event: FormEvent) => {
    event.preventDefault();
    const problem = accountError();
    if (problem) {
      setError(problem);
      return;
    }
    setWorking(true);
    setError(null);
    const phone = Utils.formatPhone(draft.phone);
    try {
      await signUp({
        username: phone,
        password: draft.password,
        options: {
          userAttributes: {
            phone_number: phone,
            email: draft.email.trim(),
          },
        },
      });
      update({ phone, code: '' });
      setStep('confirm');
    } catch (signUpError) {
      if (errorName(signUpError) === 'UsernameExistsException') {
        try {
          await signInAndSubmit();
          return;
        } catch {
          setError(ERROR_ACCOUNT_EXISTS);
        }
      } else {
        setError(errorMessage(signUpError) || ERROR_SIGNUP);
      }
    } finally {
      setWorking(false);
    }
  };

  const handleConfirm = async (event: FormEvent) => {
    event.preventDefault();
    if (draft.code.trim().length < 6) {
      setError(ERROR_CODE);
      return;
    }
    setWorking(true);
    setError(null);
    try {
      await confirmSignUp({
        username: Utils.formatPhone(draft.phone),
        confirmationCode: draft.code.trim(),
      });
      await signInAndSubmit();
    } catch (confirmError) {
      const name = errorName(confirmError);
      if (name === 'CodeMismatchException') {
        setError(ERROR_CODE_WRONG);
      } else if (
        name === 'NotAuthorizedException' &&
        errorMessage(confirmError).toUpperCase().includes('CONFIRMED')
      ) {
        try {
          await signInAndSubmit();
          return;
        } catch {
          setError(ERROR_SUBMIT);
        }
      } else {
        setError(errorMessage(confirmError) || ERROR_CONFIRM);
      }
    } finally {
      setWorking(false);
    }
  };

  const handleResend = async () => {
    setWorking(true);
    setError(null);
    try {
      await resendSignUpCode({ username: Utils.formatPhone(draft.phone) });
      toast.success(RESENT_TEXT);
    } catch {
      setError(ERROR_RESEND);
    } finally {
      setWorking(false);
    }
  };

  const handleJoinSignedIn = async (event: FormEvent) => {
    event.preventDefault();
    const problem = accountError();
    if (problem) {
      setError(problem);
      return;
    }
    setWorking(true);
    setError(null);
    try {
      await submitRegistration();
    } catch {
      setError(ERROR_SUBMIT);
      setWorking(false);
    }
  };

  const title = circleTitle(schema?.tribe_name);

  const renderBody = () => {
    if (loadError) {
      return (
        <div className={styles.card}>
          <p className={styles.error} role="alert">
            {loadError}
          </p>
        </div>
      );
    }
    if (!schema || step === 'checking') {
      return (
        <div className={styles.card}>
          <div className={styles.loading} role="status">
            <CircularProgress size={28} />
            <span>{REGISTRATION_LOADING_TEXT}</span>
          </div>
        </div>
      );
    }

    if (step === 'confirm') {
      return (
        <form className={styles.card} onSubmit={handleConfirm}>
          <h1 className={styles.title}>{CONFIRM_TITLE}</h1>
          <p className={styles.text}>{CONFIRM_TEXT(draft.phone)}</p>
          <label className={styles.field}>
            <span className={styles.label}>{CODE_LABEL}</span>
            <input
              className={styles.codeInput}
              value={draft.code}
              placeholder={CODE_PLACEHOLDER}
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={working}
              onChange={(event) => update({ code: event.target.value })}
            />
          </label>
          {error ? (
            <p className={styles.error} role="alert">
              {error}
            </p>
          ) : null}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              disabled={working}
              onClick={() => {
                setError(null);
                setStep('account');
              }}
            >
              {BACK_LABEL}
            </button>
            <button
              type="submit"
              className={styles.primaryButton}
              disabled={working}
            >
              {working ? WORKING_LABEL : CONFIRM_LABEL}
            </button>
          </div>
          <button
            type="button"
            className={styles.linkButton}
            disabled={working}
            onClick={handleResend}
          >
            {RESEND_LABEL}
          </button>
        </form>
      );
    }

    const signedIn = step === 'signedIn';
    return (
      <form
        className={styles.card}
        onSubmit={signedIn ? handleJoinSignedIn : handleCreateAccount}
      >
        <div>
          <div className={styles.kicker}>{schema.schemaJson.title}</div>
          <h1 className={styles.title}>{title}</h1>
        </div>
        <p className={styles.text}>
          {signedIn ? SIGNED_IN_INTRO(signedInName) : REGISTRATION_INTRO}
        </p>

        <label className={styles.field}>
          <span className={styles.label}>{FIRST_NAME_LABEL}</span>
          <input
            className={styles.input}
            value={draft.firstName}
            autoComplete="given-name"
            disabled={working}
            onChange={(event) => update({ firstName: event.target.value })}
          />
        </label>
        <label className={styles.field}>
          <span className={styles.label}>{SURNAME_LABEL}</span>
          <input
            className={styles.input}
            value={draft.surname}
            autoComplete="family-name"
            disabled={working}
            onChange={(event) => update({ surname: event.target.value })}
          />
        </label>
        <div className={styles.field}>
          <span className={styles.label}>{PHONE_LABEL}</span>
          <div className={styles.phone}>
            <DunbarPhoneInput
              country="no"
              value={draft.phone.replace(/^\+/, '')}
              onChange={(value: string) => update({ phone: `+${value}` })}
              disabled={working || signedIn}
              inputProps={{ 'aria-label': PHONE_LABEL }}
            />
          </div>
        </div>
        <label className={styles.field}>
          <span className={styles.label}>{EMAIL_LABEL}</span>
          <input
            className={styles.input}
            type="email"
            value={draft.email}
            autoComplete="email"
            disabled={working}
            onChange={(event) => update({ email: event.target.value })}
          />
        </label>
        {signedIn ? null : (
          <label className={styles.field}>
            <span className={styles.label}>{PASSWORD_LABEL}</span>
            <input
              className={styles.input}
              type="password"
              value={draft.password}
              autoComplete="new-password"
              disabled={working}
              onChange={(event) => update({ password: event.target.value })}
            />
          </label>
        )}
        {signedIn ? null : <p className={styles.quiet}>{PASSWORD_HINT}</p>}

        <label className={styles.consent}>
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={draft.consent}
            disabled={working}
            onChange={(event) => update({ consent: event.target.checked })}
          />
          <span>{CONSENT_LABEL}</span>
        </label>
        <button
          type="button"
          className={styles.linkButton}
          onClick={() => setPrivacyOpen(true)}
        >
          {PRIVACY_LINK_LABEL}
        </button>

        {error ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        <div className={styles.actions}>
          <button
            type="submit"
            className={styles.primaryButton}
            disabled={working}
          >
            {working
              ? WORKING_LABEL
              : signedIn
                ? JOIN_NOW_LABEL
                : CREATE_ACCOUNT_LABEL}
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className={styles.page}>
      <main className={styles.content}>
        <div className={styles.brand}>Dunbar148</div>
        {renderBody()}
      </main>
      <MuiModal open={privacyOpen} setOpen={setPrivacyOpen} width={560}>
        <div className={styles.modal}>
          <h2 className={styles.modalTitle}>{PRIVACY_TITLE}</h2>
          {PRIVACY_PARAGRAPHS.map((paragraph) => (
            <p key={paragraph} className={styles.text}>
              {paragraph}
            </p>
          ))}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setPrivacyOpen(false)}
            >
              {CLOSE_LABEL}
            </button>
          </div>
        </div>
      </MuiModal>
    </div>
  );
};
